import { useCallback, useEffect, useRef } from 'react';

import type { LineItem } from '@getjust/gateway';

import { useDiscountCodesWatch } from '$src/hooks/client/useDiscountCodesWatch';
import { useAddLineItems, useDeleteLineItems } from '$src/hooks/mutations';
import { useCart } from '$src/hooks/queries';
import { useGetPDDMetaobjectDiscount } from '$src/hooks/queries/usePDDMetaobjectDiscount';

import { useCheckoutFeaturesAtom } from '../state/useCheckoutFeaturesAtom';

/**
 * Check if variants were already added by this discount code
 * Matches products that have:
 * - The variant ID from the metaobject AND
 * - Either our custom attribute (_zax_add_with_code) OR no custom attributes at all
 * This handles products added by Shopify (no attributes) or by Just (with attribute)
 */
const findExistingLineItemsForDiscount = (
  lineItems: LineItem[] | undefined,
  discountCode: string,
  variants: string[],
): string[] => {
  if (!lineItems) return [];

  return lineItems
    .filter((item) => {
      // Check if variant matches the metaobject variants
      const variantMatches = variants.includes(item.variantId);

      // Check if has our custom attribute (added by Just)
      const hasDiscountAttribute = item.customAttributes?.some(
        (attr) => attr.key === '_zax_add_with_code' && attr.value === discountCode,
      );

      // Match if: variant matches AND (has our attribute OR no custom attributes at all)
      // This handles products added by Shopify (no attributes) or by Just (with attribute)
      const hasNoCustomAttributes = (item.customAttributes?.length ?? 0) === 0;
      const isLikelyDiscountProduct = variantMatches && (hasDiscountAttribute || hasNoCustomAttributes);

      return isLikelyDiscountProduct;
    })
    .map((item) => item.id);
};

/**
 * Hook to handle discount code metaobject validation and product addition
 * This hook monitors discount code changes and automatically adds/removes products
 * based on metaobject configuration
 * This is a custom hook for PDD shop - INT-293
 */
export const usePDDMetaobjectDiscount = () => {
  const { canUseCheckoutFeature } = useCheckoutFeaturesAtom();
  const isFeatureEnabled = canUseCheckoutFeature('zax_checkout_discount_code_add_products');

  const { data: cart } = useCart();
  const { addedDiscountCodes, removedDiscountCodes } = useDiscountCodesWatch();
  const { getDiscountMetaobject } = useGetPDDMetaobjectDiscount();
  const { mutateAsync: addLineItems } = useAddLineItems();
  const { mutateAsync: deleteLineItems } = useDeleteLineItems();

  // Track products added by discount codes for cleanup
  const discountProducts = useRef<Map<string, string[]>>(new Map());
  const lastProcessedCartId = useRef<string | undefined>(undefined);

  const handleDiscountCodesAdded = useCallback(
    async (discountCodes: string[]) => {
      if (!isFeatureEnabled || !cart?.id || discountCodes.length === 0) {
        return;
      }

      // Process codes sequentially
      for (const discountCode of discountCodes) {
        try {
          // Skip if already processed (idempotency check to prevent duplicate additions)
          if (discountProducts.current.has(discountCode)) {
            console.warn('[DiscountCodeHandler] Discount code already processed, skipping:', discountCode);
            continue;
          }

          console.warn('[DiscountCodeHandler] Processing added discount code:', discountCode);

          // Query metaobject for discount code configuration
          const metaobjectResult = await getDiscountMetaobject(discountCode);

          if (metaobjectResult?.outcome !== 'found') {
            console.warn('[DiscountCodeHandler] No metaobject found for discount:', {
              discountCode,
              reason: metaobjectResult?.reason,
            });
            continue;
          }

          const { variants, quantities } = metaobjectResult;

          if (!variants || !quantities || variants.length !== quantities.length) {
            console.warn('[DiscountCodeHandler] Invalid metaobject format:', {
              discountCode,
              variantsCount: variants?.length,
              quantitiesCount: quantities?.length,
            });
            continue;
          }

          // Check if products were already added (e.g., on Shopify before coming to Just)
          const existingLineItemIds = findExistingLineItemsForDiscount(
            cart?.lineItems,
            discountCode,
            variants,
          );

          if (existingLineItemIds.length > 0) {
            console.warn('[DiscountCodeHandler] Products already in cart for discount:', {
              discountCode,
              existingLineItemIds,
            });
            // Track existing items for future removal (adoption pattern)
            discountProducts.current.set(discountCode, existingLineItemIds);
            continue; // Skip adding, already present
          }

          // Create line items for the variants
          const lineItems = variants
            .filter((variantId, index) => Boolean(variantId) && Boolean(quantities[index]))
            .map((variantId, index) => ({
              variantId,
              quantity: Number(quantities[index]),
              // These custom attributes are essential for PDD free product addition
              customAttributes: [
                {
                  key: '_zax_add_with_code',
                  value: discountCode,
                },
                {
                  key: '_zax_add_with_code_discount',
                  value: 'true',
                },
                {
                  key: '_zax_add_with_code_quantity',
                  value: quantities[index]?.toString() ?? '0',
                },
              ],
            }));

          console.warn('[DiscountCodeHandler] Adding products for discount:', {
            discountCode,
            lineItems,
          });

          // Add the line items to the cart
          const addResult = await addLineItems({ lineItems });

          if (!addResult.data?.lineItems) {
            console.warn('[DiscountCodeHandler] No line items added for discount:', {
              discountCode,
            });
            continue;
          }

          // Track the added line items for future removal
          const addedLineItemIds = addResult.data.lineItems
            .filter((item) => variants.includes(item.variantId))
            .map((item) => item.id);

          if (addedLineItemIds.length === 0) {
            console.warn('[DiscountCodeHandler] No line items added for discount:', {
              discountCode,
            });
            continue;
          }

          discountProducts.current.set(discountCode, addedLineItemIds);
          console.warn('[DiscountCodeHandler] Products added and tracked:', {
            discountCode,
            lineItemIds: addedLineItemIds,
          });
        } catch (error) {
          console.error('[DiscountCodeHandler] Error processing added discount:', {
            discountCode,
            error,
          });
        }
      }
    },
    [isFeatureEnabled, cart?.id, cart?.lineItems, getDiscountMetaobject, addLineItems],
  );

  const handleDiscountCodesRemoved = useCallback(
    async (discountCodes: string[]) => {
      if (!isFeatureEnabled || !cart?.id || discountCodes.length === 0) {
        return;
      }

      // Process discount codes sequentially
      for (const discountCode of discountCodes) {
        try {
          console.warn('[DiscountCodeHandler] Processing removed discount code:', discountCode);

          // Get the line items that were added by this discount code
          const addedLineItemIds = discountProducts.current.get(discountCode);

          if (!addedLineItemIds || addedLineItemIds.length === 0) {
            console.warn('[DiscountCodeHandler] No tracked products for discount:', discountCode);
            continue;
          }

          // Check which line items still exist in the cart
          const currentLineItemIds = cart.lineItems?.map((item) => item.id) ?? [];
          const inCartProducts = addedLineItemIds.filter((id) => currentLineItemIds.includes(id));

          if (inCartProducts.length === 0) {
            console.warn('[DiscountCodeHandler] No products to remove (manually removed):', discountCode);
            // Clean up tracking
            discountProducts.current.delete(discountCode);
            continue;
          }

          console.warn('[DiscountCodeHandler] Removing products for discount:', {
            discountCode,
            lineItemIds: inCartProducts,
          });

          // Remove the line items
          await deleteLineItems({ lineItemIds: inCartProducts });

          console.warn('[DiscountCodeHandler] Products removed for discount:', discountCode);

          // Clean up tracking
          discountProducts.current.delete(discountCode);
        } catch (error) {
          console.error('[DiscountCodeHandler] Error processing removed discount:', {
            discountCode,
            error,
          });
        }
      }
    },
    [isFeatureEnabled, cart?.id, cart?.lineItems, deleteLineItems],
  );

  // Handle added discount codes
  useEffect(() => {
    if (isFeatureEnabled && addedDiscountCodes.length > 0) {
      handleDiscountCodesAdded(addedDiscountCodes);
    }
  }, [isFeatureEnabled, addedDiscountCodes, handleDiscountCodesAdded]);

  // Handle removed discount codes
  useEffect(() => {
    if (isFeatureEnabled && removedDiscountCodes.length > 0) {
      handleDiscountCodesRemoved(removedDiscountCodes);
    }
  }, [isFeatureEnabled, removedDiscountCodes, handleDiscountCodesRemoved]);

  // Clear discount products tracking when cart ID changes
  useEffect(() => {
    if (cart?.id !== lastProcessedCartId.current) {
      discountProducts.current.clear();
      lastProcessedCartId.current = cart?.id;
    }
  }, [cart?.id]);

  // Cleanup on unmount
  useEffect(() => {
    const discountProductsRef = discountProducts.current;
    return () => {
      discountProductsRef.clear();
    };
  }, []);

  return {
    discountProducts: discountProducts.current,
  };
};
